import { useState } from 'react'
import { api } from '../api/client'
import type { Fingerprint, ProxyConfig } from '../types/profile'

interface Props {
  onCreated: () => void
}

const PLATFORMS: Fingerprint['platform'][] = ['windows', 'macos', 'linux']
const BRANDS: Fingerprint['brand'][] = ['Chrome', 'Edge', 'Opera', 'Vivaldi', 'Chromium']
const TIMEZONES = [
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'UTC',
]
const LANGUAGES = ['zh-CN', 'en-US', 'ja-JP', 'ko-KR', 'fr-FR', 'de-DE']

export function CreateProfile({ onCreated }: Props) {
  const [name, setName] = useState('')
  const [group, setGroup] = useState('')
  const [tags, setTags] = useState('')
  const [platform, setPlatform] = useState<Fingerprint['platform']>('windows')
  const [brand, setBrand] = useState<Fingerprint['brand']>('Chrome')
  const [language, setLanguage] = useState('zh-CN')
  const [timezone, setTimezone] = useState('Asia/Shanghai')
  const [screenWidth, setScreenWidth] = useState(1920)
  const [screenHeight, setScreenHeight] = useState(1080)
  const [hardwareConcurrency, setHardwareConcurrency] = useState(8)
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1_000_000))

  const [proxyEnabled, setProxyEnabled] = useState(false)
  const [proxyServer, setProxyServer] = useState('')
  const [proxyUsername, setProxyUsername] = useState('')
  const [proxyPassword, setProxyPassword] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('请填写 profile 名称')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const fingerprint: Fingerprint = {
        platform,
        brand,
        language,
        acceptLanguage: language,
        timezone,
        screenWidth,
        screenHeight,
        colorDepth: 24,
        hardwareConcurrency,
        seed,
      }
      let proxy: ProxyConfig | undefined
      if (proxyEnabled && proxyServer.trim()) {
        proxy = {
          enabled: true,
          server: proxyServer.trim(),
          username: proxyUsername || undefined,
          password: proxyPassword || undefined,
          bypass: ['localhost', '127.0.0.1'],
        }
      }
      await api.createProfile({
        name: name.trim(),
        group: group.trim() || undefined,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        fingerprint,
        proxy,
      })
      onCreated()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <h2>新建 Profile</h2>
      {error && <div className="error-banner">{error}</div>}

      <fieldset>
        <legend>基础信息</legend>
        <label>
          名称 *
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：账号A-主号"
            required
          />
        </label>
        <label>
          分组
          <input
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder="例如：跨境电商"
          />
        </label>
        <label>
          标签（逗号分隔）
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="shopee, 主号"
          />
        </label>
      </fieldset>

      <fieldset>
        <legend>指纹配置</legend>
        <div className="grid-2">
          <label>
            操作系统
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Fingerprint['platform'])}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>
          <label>
            浏览器品牌
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value as Fingerprint['brand'])}
            >
              {BRANDS.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </label>
          <label>
            语言
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </label>
          <label>
            时区
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {TIMEZONES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label>
            屏幕宽度
            <input
              type="number"
              min={320}
              max={7680}
              value={screenWidth}
              onChange={(e) => setScreenWidth(parseInt(e.target.value, 10))}
            />
          </label>
          <label>
            屏幕高度
            <input
              type="number"
              min={240}
              max={4320}
              value={screenHeight}
              onChange={(e) => setScreenHeight(parseInt(e.target.value, 10))}
            />
          </label>
          <label>
            CPU 核数
            <input
              type="number"
              min={1}
              max={128}
              value={hardwareConcurrency}
              onChange={(e) => setHardwareConcurrency(parseInt(e.target.value, 10))}
            />
          </label>
          <label>
            种子（决定性指纹）
            <input
              type="number"
              value={seed}
              onChange={(e) => setSeed(parseInt(e.target.value, 10))}
            />
          </label>
        </div>
        <button type="button" onClick={() => setSeed(Math.floor(Math.random() * 1_000_000))}>
          随机生成种子
        </button>
      </fieldset>

      <fieldset>
        <legend>代理（可选）</legend>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={proxyEnabled}
            onChange={(e) => setProxyEnabled(e.target.checked)}
          />
          启用代理
        </label>
        {proxyEnabled && (
          <>
            <label>
              服务器
              <input
                value={proxyServer}
                onChange={(e) => setProxyServer(e.target.value)}
                placeholder="socks5://user:pass@host:1080"
              />
            </label>
            <label>
              用户名（可选）
              <input
                value={proxyUsername}
                onChange={(e) => setProxyUsername(e.target.value)}
              />
            </label>
            <label>
              密码（可选）
              <input
                type="password"
                value={proxyPassword}
                onChange={(e) => setProxyPassword(e.target.value)}
              />
            </label>
          </>
        )}
      </fieldset>

      <div className="form-actions">
        <button type="submit" disabled={busy}>
          {busy ? '创建中…' : '创建 Profile'}
        </button>
      </div>
    </form>
  )
}